import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
    type ReactNode,
} from "react";
import { useAtom, useAtomValue, useSetAtom, type Atom, type PrimitiveAtom, type WritableAtom } from "jotai";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { AutomationStepEditArea } from "@/routes/automations/automation-edit-page/AutomationStepEditArea";
import { AutomationStepList } from "@/components/automations/AutomationStepList";
import { AutomationEmptyStepCreator } from "@/routes/automations/automation-edit-page/AutomationEmptyStepCreator";
import { StepListContainer } from "@/routes/automations/automation-details-page/StepListContainer";
import { cn } from "@/components/podkit/lib/cn";
import { NewAutomationStepButton } from "@/routes/automations/automation-edit-page/NewAutomationStepButton";
import {
    AutomationTriggerEditArea,
    type AutomationTriggerEditData,
} from "@/components/automations/AutomationTriggerEditArea";
import type { PlainWorkflow } from "@/queries/automation-queries";
import type {
    WorkflowEditFormStepData,
    WorkflowEditFormStepType,
    WorkflowEditFormTriggerData,
} from "@/components/automations/automation-edit-form-data";
import type { DraftStep, SidePanelEditingState } from "@/routes/automations/automation-edit-page/types";

const STEP_EDIT_AREA_WIDTH = 640;

type AutomationDefinitionSectionEditProps = {
    workflow: PlainWorkflow;
    editableStepsAtom: PrimitiveAtom<WorkflowEditFormStepData[] | null>;
    editableTriggersAtom: PrimitiveAtom<WorkflowEditFormTriggerData[]>;
    editableExecutorAtom: PrimitiveAtom<PlainMessage<Subject> | undefined>;
    editableMaxParallelAtom: PrimitiveAtom<number | undefined>;
    editableMaxTotalAtom: PrimitiveAtom<number | undefined>;
    sidePanelEditingAtom: PrimitiveAtom<SidePanelEditingState>;
    newStepAtom: PrimitiveAtom<WorkflowEditFormStepData | null>;
    draftStepAtom: PrimitiveAtom<DraftStep | null>;
    isCreatingStepAtom: Atom<boolean>;
    removeStepAtom: WritableAtom<null, [number], void>;
    createNewStepAtom: WritableAtom<null, [WorkflowEditFormStepType?], void>;
    createStepIfEmptyAtomEffect: Atom<void>;
    createDraftAtomEffect: Atom<void>;
    changeDraftStepTypeAtom: WritableAtom<null, [WorkflowEditFormStepType], void>;
    upsertDraftStepIntoStepsAtom: WritableAtom<null, [], boolean | undefined>;
};

type AnimatedSidePanelHandle = {
    close: () => Promise<void>;
};

export const AutomationDefinitionSectionEdit: FC<AutomationDefinitionSectionEditProps> = ({
    workflow,
    editableStepsAtom,
    editableTriggersAtom,
    editableExecutorAtom,
    editableMaxParallelAtom,
    editableMaxTotalAtom,
    sidePanelEditingAtom,
    newStepAtom,
    draftStepAtom,
    isCreatingStepAtom,
    removeStepAtom,
    createNewStepAtom,
    createStepIfEmptyAtomEffect,
    createDraftAtomEffect,
    changeDraftStepTypeAtom,
    upsertDraftStepIntoStepsAtom,
}) => {
    useAtom(createStepIfEmptyAtomEffect);
    useAtom(createDraftAtomEffect);

    const [isClosingSidePanel, setIsClosingSidePanel] = useState(false);
    const [sidePanelEditing, setSidePanelEditing] = useAtom(sidePanelEditingAtom);
    const editableSteps = useAtomValue(editableStepsAtom);
    const draftStep = useAtomValue(draftStepAtom);
    const setNewStep = useSetAtom(newStepAtom);
    const setExecutor = useSetAtom(editableExecutorAtom);
    const setTriggers = useSetAtom(editableTriggersAtom);
    const setMaxParallel = useSetAtom(editableMaxParallelAtom);
    const setMaxTotal = useSetAtom(editableMaxTotalAtom);

    const sidePanelRef = useRef<AnimatedSidePanelHandle>(null);

    const isEditingTrigger = sidePanelEditing?.type === "trigger";
    const isEditSidePanelVisible = (draftStep !== null || isEditingTrigger) && !isClosingSidePanel;

    const closeSidePanel = useCallback(async () => {
        if (sidePanelRef.current) {
            setIsClosingSidePanel(true);
            await sidePanelRef.current.close();
            setIsClosingSidePanel(false);
            setNewStep(null);
            setSidePanelEditing(null);
        }
    }, [setNewStep, setSidePanelEditing]);

    const handleTriggerUpdate = useCallback(
        async (data: AutomationTriggerEditData) => {
            setExecutor(data.executor);
            setTriggers(data.triggers);
            setMaxParallel(data.maxParallel);
            setMaxTotal(data.maxTotal);
            await closeSidePanel();
        },
        [setExecutor, setTriggers, setMaxParallel, setMaxTotal, closeSidePanel],
    );

    if (editableSteps === null) {
        return null;
    }

    console.log({ draftStep });

    let sideContent: ReactNode | null = null;
    if (draftStep === null && !isClosingSidePanel && !isEditingTrigger) {
        sideContent = null;
    } else if (isEditingTrigger) {
        sideContent = (
            <AnimatedSidePanel ref={sidePanelRef} panelWidth={STEP_EDIT_AREA_WIDTH}>
                <AutomationTriggerEditArea
                    workflow={workflow}
                    executorAtom={editableExecutorAtom}
                    triggersAtom={editableTriggersAtom}
                    maxParallelAtom={editableMaxParallelAtom}
                    maxTotalAtom={editableMaxTotalAtom}
                    onUpdate={handleTriggerUpdate}
                    onCancel={closeSidePanel}
                />
            </AnimatedSidePanel>
        );
    } else {
        sideContent = (
            <AnimatedSidePanel ref={sidePanelRef} panelWidth={STEP_EDIT_AREA_WIDTH}>
                <AutomationStepEditArea
                    sidePanelEditingAtom={sidePanelEditingAtom}
                    draftStepAtom={draftStepAtom}
                    isCreatingStepAtom={isCreatingStepAtom}
                    removeStepAtom={removeStepAtom}
                    changeDraftStepTypeAtom={changeDraftStepTypeAtom}
                    upsertDraftStepIntoStepsAtom={upsertDraftStepIntoStepsAtom}
                    onClose={closeSidePanel}
                />
            </AnimatedSidePanel>
        );
    }

    return (
        <div
            className={cn(
                "grid grow grid-rows-1 overflow-hidden border-t border-border-base",
                "ease-out-expo transition-all duration-150",
                isEditSidePanelVisible ? "grid-cols-[1fr_640px]" : "grid-cols-[1fr_0px]",
            )}
        >
            <StepListContainer className="flex-[3] grow rounded-none border-0">
                {editableSteps.length === 0 ? (
                    <AutomationEmptyStepCreator
                        draftStepAtom={draftStepAtom}
                        changeDraftStepTypeAtom={changeDraftStepTypeAtom}
                        upsertDraftStepIntoStepsAtom={upsertDraftStepIntoStepsAtom}
                    />
                ) : (
                    <div className="flex w-full max-w-screen-xl grow flex-row items-start justify-center gap-4">
                        <EditableAutomationStepList
                            editableStepsAtom={editableStepsAtom}
                            editableTriggersAtom={editableTriggersAtom}
                            editableExecutorAtom={editableExecutorAtom}
                            editableMaxParallelAtom={editableMaxParallelAtom}
                            editableMaxTotalAtom={editableMaxTotalAtom}
                            sidePanelEditingAtom={sidePanelEditingAtom}
                            removeStepAtom={removeStepAtom}
                            isCreatingStepAtom={isCreatingStepAtom}
                            createNewStepAtom={createNewStepAtom}
                        />
                    </div>
                )}
            </StepListContainer>
            {sideContent}
        </div>
    );
};

const EditableAutomationStepList: FC<{
    editableStepsAtom: PrimitiveAtom<WorkflowEditFormStepData[] | null>;
    editableTriggersAtom: PrimitiveAtom<WorkflowEditFormTriggerData[]>;
    editableExecutorAtom: PrimitiveAtom<PlainMessage<Subject> | undefined>;
    editableMaxParallelAtom: PrimitiveAtom<number | undefined>;
    editableMaxTotalAtom: PrimitiveAtom<number | undefined>;
    sidePanelEditingAtom: PrimitiveAtom<SidePanelEditingState>;
    removeStepAtom: WritableAtom<null, [number], void>;
    isCreatingStepAtom: Atom<boolean>;
    createNewStepAtom: WritableAtom<null, [WorkflowEditFormStepType?], void>;
}> = ({
    editableStepsAtom,
    editableTriggersAtom,
    editableExecutorAtom,
    editableMaxParallelAtom,
    editableMaxTotalAtom,
    sidePanelEditingAtom,
    removeStepAtom,
    isCreatingStepAtom,
    createNewStepAtom,
}) => {
    const isCreatingStep = useAtomValue(isCreatingStepAtom);
    const [editableSteps, setEditableSteps] = useAtom(editableStepsAtom);
    const [sidePanelEditing, setSidePanelEditing] = useAtom(sidePanelEditingAtom);
    const triggers = useAtomValue(editableTriggersAtom);
    const maxParallel = useAtomValue(editableMaxParallelAtom);
    const maxTotal = useAtomValue(editableMaxTotalAtom);
    const removeStep = useSetAtom(removeStepAtom);
    const createNewStep = useSetAtom(createNewStepAtom);
    const executor = useAtomValue(editableExecutorAtom);

    const stepIndexBeingEdited = sidePanelEditing?.type === "step" ? sidePanelEditing.index : null;

    const handleStepSelect = (index: number) => {
        setSidePanelEditing({ type: "step", index });
    };

    const handleStepsReorder = (newSteps: WorkflowEditFormStepData[]) => {
        setEditableSteps(newSteps);
        if (stepIndexBeingEdited !== null) {
            const currentStep = editableSteps?.[stepIndexBeingEdited];
            if (currentStep) {
                const newIndex = newSteps.findIndex((s) => s.id === currentStep.id);
                if (newIndex !== -1 && newIndex !== stepIndexBeingEdited) {
                    setSidePanelEditing({ type: "step", index: newIndex });
                }
            }
        }
    };

    const openEditTriggerSidePanel = () => {
        setSidePanelEditing({ type: "trigger" });
    };

    const limits = maxParallel !== undefined || maxTotal !== undefined ? { maxParallel, maxTotal } : undefined;
    const isTriggerSelected = sidePanelEditing?.type === "trigger";

    return (
        <AutomationStepList
            editable
            trigger={triggers[0]}
            executor={executor}
            limits={limits}
            steps={editableSteps ?? []}
            isTriggerSelected={isTriggerSelected}
            selectedStepIndex={stepIndexBeingEdited}
            onTriggerSelect={openEditTriggerSidePanel}
            onStepSelect={handleStepSelect}
            onStepRemove={removeStep}
            onStepsReorder={handleStepsReorder}
            footer={<NewAutomationStepButton isAddingStep={isCreatingStep} onAddStep={createNewStep} />}
        />
    );
};

const AnimatedSidePanel = forwardRef<
    AnimatedSidePanelHandle,
    PropsWithChildren<{
        panelWidth: number;
    }>
>(({ children, panelWidth }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const startTime = useRef(-1);
    const endTime = useRef(-1);
    const animationRequestIdRef = useRef<number | null>(null);

    const end = panelWidth;

    const playAnimation = useCallback(
        ({ reverse = false }: { reverse?: boolean } = {}) =>
            new Promise<void>((resolve) => {
                const nextFrame = (timestamp: number) => {
                    if (!containerRef.current) return;

                    if (startTime.current === -1) {
                        startTime.current = timestamp;
                        endTime.current = timestamp + 400;
                    }

                    const t = (timestamp - startTime.current) / (endTime.current - startTime.current);
                    const p = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
                    const progress = end * p;
                    const x = reverse ? progress : end - progress;


                    containerRef.current.style.transform = `translateX(${x}px)`;

                    if (t < 1) {
                        animationRequestIdRef.current = requestAnimationFrame(nextFrame);
                    } else {
                        startTime.current = -1;
                        endTime.current = -1;
                        resolve();
                    }
                };
                animationRequestIdRef.current = requestAnimationFrame(nextFrame);
            }),
        [end],
    );

    useEffect(() => {
        void playAnimation();
        return () => {
            if (animationRequestIdRef.current) {
                cancelAnimationFrame(animationRequestIdRef.current);
                animationRequestIdRef.current = null;
            }
        };
    }, [playAnimation]);

    const close = async () => {
        await playAnimation({ reverse: true });
    };

    useImperativeHandle(ref, () => ({
        close,
    }));

    return (
        <div
            ref={containerRef}
            className="relative flex flex-1 grow flex-col overflow-auto border-l border-border-base"
        >
            {children}
        </div>
    );
});
AnimatedSidePanel.displayName = "AnimatedSidePanel";
